import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi, setToken, getToken, setCampaignId, getCampaignId } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(getCampaignId());
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(null); // null = real role, 'admin'|'coordinator'|'driver'

  const loadUser = useCallback(async () => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    try {
      const data = await authApi.me();
      setUser(data);
      setCampaigns(data.campaigns || []);
      const campaignStillAllowed = data.campaigns?.some((c) => c.campaignId === getCampaignId());
      // Auto-select first campaign if none selected or after token swap.
      if ((!getCampaignId() || !campaignStillAllowed) && data.campaigns?.length > 0) {
        const firstId = data.campaigns[0].campaignId;
        setCampaignId(firstId);
        setActiveCampaign(firstId);
      }
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const { token, user: u } = await authApi.login(email, password);
    setToken(token);
    await loadUser();
    return u;
  };

  const pinLogin = async (email, pin) => {
    const { token, user: u } = await authApi.pinLogin(email, pin);
    setToken(token);
    await loadUser();
    return u;
  };

  const logout = () => {
    setToken(null);
    setCampaignId(null);
    setUser(null);
    setCampaigns([]);
    setActiveCampaign(null);
  };

  const switchCampaign = (campaignId) => {
    setCampaignId(campaignId);
    setActiveCampaign(campaignId);
  };

  return (
    <AuthContext.Provider value={{
      user, campaigns, activeCampaign, loading,
      login, pinLogin, logout, switchCampaign, loadUser,
      viewAsRole, setViewAsRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
